import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Grid,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Close,
  NavigateBefore,
  NavigateNext,
  AddCircle as AddCircleIcon,
  Fullscreen,
  CloseFullscreen,
  Public,
  Edit,
  Share,
  Check,
} from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import Comments from "./Comments";
import { useAuth } from "./authContext";
import { useProject } from "./ProjectContext";
import { useFilter } from "./FilterContext";
import TaxonomyChip from "./TaxonomyChip";
import { isCatchAllTaxonomy } from "./utils/taxonomy";
import axios from "axios";
import "./SidePanelComponent.css";
import LockIcon from "@mui/icons-material/Lock";
import MapIcon from "@mui/icons-material/Map";
import config from "./config";
import { apiCall } from "./api";
import ContributionComp from "./ContributionComp";
import { SHAPE_TYPE, VISIBILITY } from "./utils/constants";
import { getUserName } from "./utils/map.util";
import { Plyr } from "plyr-react";
import "plyr-react/plyr.css";

// Item 4 (client 2026-06-18, revised 2026-06-22): the photo + story lead the
// panel. Core metadata — Location (address) / Posted by (contributor) /
// Visibility (privacy) — stays ALWAYS visible at the top in a slightly
// smaller font. Only the taxonomy chips (Themes / Property Types /
// Communities) collapse behind a "Show metadata" toggle that defaults HIDDEN
// on every panel mount (no cross-session persistence — the client wants it
// to always start hidden).

const renderTextWithLinks = (text) => {
  if (!text || typeof text !== "string") return text;
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = text.split(urlRegex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#1a73e8", wordBreak: "break-all" }}
      >
        {part}
      </a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const VIDEO_EXTS = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv", "m4v", "3gp", "3g2", "ts", "mts", "m2ts", "mpg", "mpeg", "ogv"];
const AUDIO_EXTS = ["mp3", "wav", "aac", "flac", "ogg", "wma", "m4a", "aiff", "aif", "caf", "opus", "amr"];

// Compute distinct vertex count of a polygon ring. `loc_id` from the API is a
// JSON string of [[lat,lng], ...]; rings are often closed (first === last) so
// we drop the trailing duplicate to report the user-visible point count.
const getPolygonVertexCount = (loc_id) => {
  if (!loc_id) return 0;
  let coords = loc_id;
  if (typeof loc_id === "string") {
    try {
      coords = JSON.parse(loc_id);
    } catch (e) {
      return 0;
    }
  }
  if (!Array.isArray(coords) || coords.length === 0) return 0;
  const first = coords[0];
  const last = coords[coords.length - 1];
  const isClosed =
    Array.isArray(first) &&
    Array.isArray(last) &&
    first[0] === last[0] &&
    first[1] === last[1] &&
    coords.length > 1;
  return isClosed ? coords.length - 1 : coords.length;
};

const SidePanelComponent = ({
  isSidePanelOpen,
  setIsSidePanelOpen,
  isSidePanelFullscreen,
  setIsSidePanelFullscreen,
  year,
  setYear,
  setSelectedMarker,
  setSelectedAddressLatLng,
  setSelectedAddress,
  setStoryVisibility,
  setShowContribution,
  markerIds,
  refreshData,
  setIsEditing,
  isEditing,
  polygonData,
  lineData,
  setAddStoryMode,
  selectedAddressLatLng,
  locationData,
  isExhibitionMode,
  setSelectionType,
  setPolygonCoordinates,
  setLineCoordinates,
  polygons,
  polylines,
  pendingFocusShapeId,
  onPendingFocusConsumed,
}) => {
  const { user, globalRoles, projectRoles } = useAuth();
  const { project } = useProject();
  // Filter context: we only need the chip catalogue (taxonomyIndex) to
  // resolve name+color for a story's assigned taxonomy_ids. The chips on
  // the side panel are read-only descriptors of the story, NOT controls
  // for the global filter — so we don't pull toggleTheme/togglePropertyType
  // here. Filter mutations live exclusively on the top filter bar.
  const { taxonomyIndex } = useFilter();
  const [data, setData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [carouselFiles, setCarouselFiles] = useState([]);
  const [nonImageFiles, setNonImageFiles] = useState([]);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  // Item 4: "Show metadata" disclosure state. Defaults HIDDEN on every panel
  // mount (client 2026-06-22); the choice is NOT persisted across reloads, so
  // the metadata always starts collapsed. It still sticks while the user
  // browses between entries in the same open panel session.
  const [showMetadata, setShowMetadata] = useState(false);
  const toggleShowMetadata = useCallback(() => {
    setShowMetadata((prev) => !prev);
  }, []);

  const [availableYears, setAvailableYears] = useState([]);
  const [, setCurrentYear] = useState(null);
  const projectMapLabel = project?.project_name
    ? `${project.project_name}`
    : "Project Map";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setData([]);

        if (polygonData?.id) {
          // Find all polygons at the same coordinates
          const currentPolygon = Array.isArray(polygons)
            ? polygons.find((p) => p.id === polygonData.id)
            : null;

          let polygonIdToFetch = polygonData.id;

          if (currentPolygon) {
            const currentCoords = JSON.stringify(currentPolygon.coordinates);
            const currentDecade = Math.floor(year / 10) * 10;

            // Find the polygon at the same coordinates whose year matches the current decade
            const matchForDecade = polygons.find(
              (p) =>
                JSON.stringify(p.coordinates) === currentCoords &&
                Math.floor(Number(p.year) / 10) * 10 === currentDecade
            );

            if (matchForDecade) {
              polygonIdToFetch = matchForDecade.id;
            } else {
              // No polygon for this decade at these coordinates -- show empty state
              return;
            }
          }

          // Auth refactor 2026-05-27: stopped appending &uid= — the server
          // now derives the viewer's uid from the verified Firebase ID
          // token via firebase_auth.php. apiCall() attaches the token
          // header automatically (anonymous viewers send no header and
          // get the public-only visibility path).
          const rawData = await apiCall("getPolygonData.php", {
            query: { id: polygonIdToFetch, projectsid: project.projectsid },
          });
          if (!rawData?.error) {
            // ISSUE-001/002: getPolygonData.php now returns a `stories` array of
            // every polygon row sharing (loc_id, projectsid, year). Each entry is
            // a separate story with its own visibility / approval_status, mirroring
            // how pullsidepanel.php returns marker stories. SidePanelComponent
            // already paginates `selectedItems`, so spreading them in unlocks
            // "1 of N" navigation for polygons identical to markers.
            const stories = Array.isArray(rawData?.stories) && rawData.stories.length
              ? rawData.stories
              : [rawData];
            setData(stories);
          }
          return;
        }

        if (lineData?.id) {
          // Mirror the polygon flow: pick the polyline at the same coordinates
          // whose year matches the current decade, then fetch via the shared
          // getPolygonData endpoint with shape_type=line.
          const currentPolyline = Array.isArray(polylines)
            ? polylines.find((p) => p.id === lineData.id)
            : null;

          let polylineIdToFetch = lineData.id;

          if (currentPolyline) {
            const currentCoords = JSON.stringify(currentPolyline.coordinates);
            const currentDecade = Math.floor(year / 10) * 10;

            const matchForDecade = polylines.find(
              (p) =>
                JSON.stringify(p.coordinates) === currentCoords &&
                Math.floor(Number(p.year) / 10) * 10 === currentDecade
            );

            if (matchForDecade) {
              polylineIdToFetch = matchForDecade.id;
            } else {
              return;
            }
          }

          // Same auth-refactor pattern as the polygon branch above.
          const rawData = await apiCall("getPolygonData.php", {
            query: {
              id: polylineIdToFetch,
              projectsid: project.projectsid,
              shape_type: "line",
            },
          });
          if (!rawData?.error) {
            const stories = Array.isArray(rawData?.stories) && rawData.stories.length
              ? rawData.stories
              : [rawData];
            setData(stories);
          }
          return;
        }

        if (markerIds && markerIds.length > 0) {
          // Same auth-refactor pattern as the polygon/polyline branches.
          const query = {
            markerIds: markerIds.join(","),
            projectsid: project.projectsid,
            year,
          };
          if (isExhibitionMode) query.mode = "exhibition";
          const rawData = await apiCall("pullsidepanel.php", { query });
          setData(rawData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (isSidePanelOpen) {
      fetchData();
    }
  }, [
    isSidePanelOpen,
    markerIds,
    polygonData,
    polygons,
    lineData,
    polylines,
    project.projectsid,
    year,
    user?.uid,
    isExhibitionMode,
  ]);

  // ISSUE-004: pendingFocusShapeId lets a parent (e.g. WizardContribution
  // after a successful submit) request that the side panel land on the page
  // for a specific story id when the next data load completes.
  //
  // We mirror the prop into a local "sticky" ref that we keep alive across
  // *multiple* data refreshes until the requested shape is no longer in the
  // dataset. This matters because a single submission triggers two data
  // loads back-to-back: one immediately after polygonData.id flips to the
  // new shape, and a second after refreshData() repopulates the polygons
  // array (which triggers our fetchData dep). If we consumed the ref on
  // the first load, the second load would reset currentPage back to 0 and
  // the user would land on the first story instead of the new one they
  // just submitted.
  const stickyFocusedIdRef = useRef(null);
  useEffect(() => {
    if (pendingFocusShapeId != null) {
      stickyFocusedIdRef.current = pendingFocusShapeId;
    }
  }, [pendingFocusShapeId]);

  useEffect(() => {
    if (data.length > 0) {
      setSelectedItems(data);
      let nextPage = 0;
      const wantedId = stickyFocusedIdRef.current;
      if (wantedId != null) {
        const idx = data.findIndex(
          (item) => Number(item?.id) === Number(wantedId),
        );
        if (idx >= 0) {
          nextPage = idx;
          // Tell the parent we've handled the request so it can clear its
          // state, but keep our local sticky ref so the *next* data refresh
          // (from refreshData() or a sibling re-fetch) still preserves the
          // user's focus on the new story.
          if (onPendingFocusConsumed) onPendingFocusConsumed();
        } else {
          // Wanted shape isn't in this dataset (visibility filtered it out,
          // or the user navigated to a different polygon/marker). Clear so
          // we don't keep flipping the page on unrelated reloads.
          stickyFocusedIdRef.current = null;
          if (onPendingFocusConsumed) onPendingFocusConsumed();
        }
      }
      setCurrentPage(nextPage);
    } else {
      setSelectedItems([{ year: year.toString(), fields: [] }]);
      setCurrentPage(0);
    }
  }, [data, year, onPendingFocusConsumed]);

  const fetchFiles = useCallback(async () => {
    if (
      !selectedItems ||
      !selectedItems[currentPage] ||
      !selectedItems[currentPage]?.id
    ) {
      setCarouselFiles([]);
      setNonImageFiles([]);
      return;
    }

    const currentItem = selectedItems[currentPage];
    const submissionId = currentItem.id;
    const isPolygon = currentItem.shape_type === SHAPE_TYPE.POLYGON;
    const isLine = currentItem.shape_type === SHAPE_TYPE.LINE;

    try {
      setCarouselFiles([]);
      setNonImageFiles([]);

      let filesUrl;
      if (isPolygon) {
        filesUrl = `${config.getApiUrl("getFiles.php")}?polygon_id=${submissionId}`;
      } else if (isLine) {
        filesUrl = `${config.getApiUrl("getFiles.php")}?polyline_id=${submissionId}`;
      } else {
        filesUrl = `${config.getApiUrl("getFiles.php")}?submission_id=${submissionId}`;
      }

      const response = await axios.get(filesUrl);
      const fetchedFiles = Array.isArray(response.data) ? response.data : [];

      const validFiles = fetchedFiles.filter((file) => file.filepath);

      const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "bmp", "heic", "heif", "webp", "svg", "tiff", "tif"];

      const images = [];
      const media = [];
      const others = [];

      validFiles.forEach((file) => {
        const name = (file.filename || "").toLowerCase();
        const ext = name.includes(".") ? name.split(".").pop() : "";
        if (IMAGE_EXTS.includes(ext)) {
          images.push({ id: file.id, filepath: file.filepath, caption: file.caption || "", type: "image" });
        } else if (VIDEO_EXTS.includes(ext)) {
          media.push({ id: file.id, filepath: file.filepath, caption: file.caption || "", name: file.filename || name, type: "video" });
        } else if (AUDIO_EXTS.includes(ext)) {
          media.push({ id: file.id, filepath: file.filepath, caption: file.caption || "", name: file.filename || name, type: "audio" });
        } else {
          others.push({ id: file.id, name: file.filename || name, link: file.filepath, caption: file.caption || "" });
        }
      });

      setCarouselFiles([...images, ...media]);
      setNonImageFiles(others);
    } catch (error) {
      console.error("Error fetching files:", error);
      setCarouselFiles([]);
      setNonImageFiles([]);
    }
  }, [selectedItems, currentPage]);

  useEffect(() => {
    if (selectedItems.length > 0) {
      fetchFiles();
      setCurrentCarouselIndex(0);
    }
  }, [selectedItems, currentPage, fetchFiles]);

  const handleNextCarousel = () => {
    setCurrentCarouselIndex((prevIndex) => prevIndex + 1);
  };

  const handlePreviousCarousel = () => {
    setCurrentCarouselIndex((prevIndex) => prevIndex - 1);
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setImageDialogOpen(true);
  };

  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setSelectedImage(null);
  };


  const canEditOrDelete = () => {
    const projectAdminRole = project.projectid + "_Admin";
    const currentItem = selectedItems[currentPage];

    return (
      user &&
      (globalRoles.isAdmin ||
        (projectRoles && projectRoles.includes(projectAdminRole)) ||
        (currentItem && currentItem.uid === user.uid))
    );
  };

  // Check if user has any role in the current project
  const hasProjectRole = () => {
    if (!project || !projectRoles || projectRoles.length === 0) return false;
    // Check if user has any role for this project (Admin, Mod, User, etc.)
    return projectRoles?.some((role) =>
      role.startsWith(project.projectid + "_")
    );
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditFormData({
      year: selectedItems[currentPage]?.year || year,
      visibility:
        selectedItems[currentPage]?.visibility ||
        (user ? VISIBILITY.PRIVATE : VISIBILITY.PUBLIC),
      // Default to the current year if not set
      ...selectedItems[currentPage]?.fields.reduce((acc, field) => {
        acc[field.field_name] = field.field_value || "";
        return acc;
      }, {}),
    });
  };

  // ISSUE-EDIT-PARITY: accepts an optional `draftOverride` payload so the
  // unified ContributionComp(editMode=true) can drive saves directly with its
  // own field map. When called with no argument (legacy call sites), we fall
  // back to reading `editFormData` exactly as before.
  const handleSaveChanges = async (draftOverride) => {
    try {
      const currentItem = selectedItems[currentPage];
      if (!currentItem) return false;

      const draft = draftOverride
        ? {
            year: draftOverride.year,
            visibility: draftOverride.visibility,
            fields: draftOverride.fields || {},
            // Collision-proof field map keyed by field_id (June-25 fix).
            // Present only on the unified ContributionComp(editMode) path;
            // forwarded to EDRDynamic, which prefers it over `fields`.
            fields_by_id: draftOverride.fields_by_id,
            // Item 3: taxonomy edit. Present only when ContributionComp
            // safely prefilled the picker (wipe-safety) — undefined means
            // "don't touch chips", which we honor by omitting the keys from
            // the EDRDynamic payload below.
            taxonomy_ids: draftOverride.taxonomy_ids,
            taxonomy_ranks: draftOverride.taxonomy_ranks,
          }
        : {
            year: editFormData.year,
            visibility: editFormData.visibility,
            fields: Object.keys(editFormData).reduce((acc, key) => {
              if (key !== "year" && key !== "visibility") {
                acc[key] = editFormData[key];
              }
              return acc;
            }, {}),
          };

      // Force map visibility to private/public if user doesn't have a project role
      const requestedVisibility =
        draft.visibility || (user ? VISIBILITY.PRIVATE : VISIBILITY.PUBLIC);
      const visibility =
        !hasProjectRole() && requestedVisibility === VISIBILITY.PROJECT
          ? user
            ? VISIBILITY.PRIVATE
            : VISIBILITY.PUBLIC
          : requestedVisibility;

      const payload = {
        action: "edit",
        markerId: currentItem.id,
        projectsid: Number(project.projectsid),
        year: Number(draft.year),
        originalYear: Number(currentItem.year),
        visibility: visibility,
        shape_type: currentItem.shape_type || SHAPE_TYPE.MARKER,
        fields: draft.fields,
      };
      // Prefer the field_id-keyed map when the editor supplied it so the
      // server writes each field by its unique id (duplicate field names
      // can no longer clobber each other). Omitted on legacy call sites.
      if (draft.fields_by_id && typeof draft.fields_by_id === "object") {
        payload.fields_by_id = draft.fields_by_id;
      }
      // Item 3: only send taxonomy keys when the editor supplied them.
      // EDRDynamic treats a present `taxonomy_ids` as the authoritative full
      // set (so [] clears all chips); omitting the keys leaves assignments
      // untouched. ContributionComp guarantees these are present only after a
      // safe prefill.
      const editsTaxonomy = Array.isArray(draft.taxonomy_ids);
      if (editsTaxonomy) {
        payload.taxonomy_ids = draft.taxonomy_ids;
        payload.taxonomy_ranks = Array.isArray(draft.taxonomy_ranks)
          ? draft.taxonomy_ranks
          : [];
      }

      const response = await fetch(`${config.getApiUrl("EDRDynamic.php")}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        // Build the read-shape theme_ranks overlay ({ id: rank }) from the
        // submitted [{rank,id}] list so the local panel chip strip + the map
        // repaint immediately from the selection we already know (Codex: no
        // mutable currentItem / no EDRDynamic response change needed).
        const nextThemeRanks = editsTaxonomy
          ? (draft.taxonomy_ranks || []).reduce((acc, e) => {
              if (e && e.id != null) acc[String(e.id)] = e.rank;
              return acc;
            }, {})
          : null;
        setData((prevData) => {
          const updatedData = [...prevData];
          updatedData[currentPage].fields = updatedData[currentPage].fields.map(
            (field) => ({
              ...field,
              field_value: draft.fields[field.field_name] ?? field.field_value ?? null,
            })
          );
          updatedData[currentPage].year = draft.year;
          updatedData[currentPage].visibility = visibility;
          if (editsTaxonomy) {
            updatedData[currentPage].taxonomy_ids = draft.taxonomy_ids.map(
              (id) => Number(id),
            );
            updatedData[currentPage].theme_ranks = nextThemeRanks;
          }
          return updatedData;
        });
        refreshData();
        return true;
      } else {
        alert("Failed to save changes.");
        return false;
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      alert("An error occurred while saving the changes.");
      return false;
    }
  };

  const handleExistingFileDelete = async (file) => {
    try {
      await axios.delete(`${config.getApiUrl("deleteFile.php")}`, {
        data: { file_id: file.id },
      });
      setNonImageFiles((prev) => prev.filter((f) => f.id !== file.id));
      setCarouselFiles((prev) => prev.filter((f) => f.id !== file.id));
      fetchFiles();
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  // BUG-002 (caption edit): persist a single file's caption via EDRDynamic's
  // updateFileCaption action and optimistically update the local lists so the
  // edit panel reflects the change without a full refetch (which would also
  // re-sign GCS URLs and momentarily blank the carousel).
  const handleFileCaptionSave = async (fileId, caption) => {
    if (!fileId) return false;
    try {
      const response = await fetch(`${config.getApiUrl("EDRDynamic.php")}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "updateFileCaption",
          file_id: fileId,
          caption: caption ?? "",
        }),
      });
      const result = await response.json();
      if (result?.success) {
        const stored = result.caption ?? "";
        setCarouselFiles((prev) =>
          prev.map((f) => (f.id === fileId ? { ...f, caption: stored } : f))
        );
        setNonImageFiles((prev) =>
          prev.map((f) => (f.id === fileId ? { ...f, caption: stored } : f))
        );
        return true;
      }
      console.error("Caption save failed:", result?.message);
      return false;
    } catch (error) {
      console.error("Error saving caption:", error);
      return false;
    }
  };

  const handleDelete = async () => {
    try {
      const currentItem = selectedItems[currentPage];
      if (!currentItem) return;

      const payload = {
        action: "delete",
        markerId: currentItem.id,
        projectsid: Number(project.projectsid),
        year: Number(currentItem.year),
        shape_type: currentItem.shape_type || SHAPE_TYPE.MARKER,
      };

      const response = await fetch(`${config.getApiUrl("EDRDynamic.php")}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        setData((prevData) => {
          const updatedData = [...prevData];
          updatedData.splice(currentPage, 1);
          return updatedData;
        });
        refreshData();
        setIsEditing(false);

        if (currentPage > 0) {
          setCurrentPage(currentPage - 1);
        }

        if (data.length === 1) {
          setIsSidePanelOpen(false);
        }
      } else {
        alert("Failed to delete entry.");
      }
    } catch (error) {
      console.error("Error deleting entry:", error);
      alert("An error occurred while deleting the entry.");
    }
  };

  useEffect(() => {
    const fetchAddressIfMissing = async () => {
      // Check if `selectedAddressLatLng` is set and there is no valid address field in `data`
      if (
        selectedAddressLatLng &&
        (data.length === 0 ||
          !data[0].fields.some(
            (field) => field.field_type === "address" && field.field_value
          ))
      ) {
        const { lat, lng } = selectedAddressLatLng;

        try {
          const apiKey = config.GOOGLE_MAPS_API_KEY;
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
          );
          const geocodeData = await response.json();

          if (geocodeData.results && geocodeData.results.length > 0) {
            const address = geocodeData.results[0].formatted_address;
            // Add or update the address field in `data`
            setData((prevData) => {
              const updatedData = [...prevData];

              if (updatedData.length === 0) {
                // If data is empty, initialize it with a new item containing the address
                updatedData.push({
                  fields: [
                    {
                      field_name: "Address",
                      field_value: address,
                      field_type: "address",
                    },
                  ],
                });
              } else {
                // Check if the address field already exists
                const addressField = updatedData[0].fields.find(
                  (field) => field.field_type === "address"
                );
                if (addressField) {
                  // Update the existing address field
                  addressField.field_value = address;
                } else {
                  // Add the address field if it doesn't exist
                  updatedData[0].fields.push({
                    field_name: "Address",
                    field_value: address,
                    field_type: "address",
                  });
                }
              }
              return updatedData;
            });
            setSelectedAddress(address);
          }
        } catch (error) {
          console.error("Error fetching address:", error);
        }
      }
    };

    if (isSidePanelOpen) {
      fetchAddressIfMissing();
    }
  }, [selectedAddressLatLng, isSidePanelOpen, data, setSelectedAddress]);

  const currentItem = selectedItems[currentPage] || { fields: [] };

  const handleNext = () => {
    if (currentPage < selectedItems.length - 1) {
      // ISSUE-004: user took over pagination — release the sticky focus
      // so an in-flight refresh doesn't snap them back to the post-submit
      // story.
      stickyFocusedIdRef.current = null;
      setCurrentPage(currentPage + 1);
      setCurrentCarouselIndex(0);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      stickyFocusedIdRef.current = null;
      setCurrentPage(currentPage - 1);
      setCurrentCarouselIndex(0);
    }
  };

  const fullAddress =
    currentItem.fields.find((f) => f.field_type === "address")?.field_value ||
    "";
  const shortAddress = fullAddress.split(",")[0];

  // For polygons/lines we suppress the (often empty) address field and show a
  // synthetic label like "User Defined Region (4-point polygon)" or
  // "User Defined Line (3-point line)" so users immediately understand the
  // post is a region/path, not a single point.
  const isPolygonItem = currentItem?.shape_type === SHAPE_TYPE.POLYGON;
  const isLineItem = currentItem?.shape_type === SHAPE_TYPE.LINE;
  const shapeVertexCount = (isPolygonItem || isLineItem)
    ? getPolygonVertexCount(currentItem?.loc_id)
    : 0;
  const locationDisplay = isPolygonItem
    ? shapeVertexCount > 0
      ? `User Defined Region (${shapeVertexCount}-point polygon)`
      : "User Defined Region (polygon)"
    : isLineItem
      ? shapeVertexCount > 0
        ? `User Defined Line (${shapeVertexCount}-point line)`
        : "User Defined Line"
      : fullAddress || "Unknown";

  useEffect(() => {
    if (polygonData?.id && Array.isArray(polygons)) {
      // Polygon view: derive available years from all polygons with matching coordinates
      const currentPolygon = polygons.find((p) => p.id === polygonData.id);
      if (!currentPolygon) {
        setAvailableYears([]);
        setCurrentYear(null);
        return;
      }

      const currentCoords = JSON.stringify(currentPolygon.coordinates);
      const matchingYears = new Set();
      polygons.forEach((p) => {
        if (JSON.stringify(p.coordinates) === currentCoords) {
          matchingYears.add(Math.floor(Number(p.year) / 10) * 10);
        }
      });

      const sortedYears = Array.from(matchingYears).sort((a, b) => a - b);
      setAvailableYears(sortedYears);

      const decade = Math.floor(year / 10) * 10;
      if (sortedYears.includes(decade)) {
        setCurrentYear(decade);
      } else if (sortedYears.length > 0) {
        const closestYear = sortedYears.reduce((prev, curr) =>
          Math.abs(curr - decade) < Math.abs(prev - decade) ? curr : prev
        );
        setCurrentYear(closestYear);
      } else {
        setCurrentYear(null);
      }
      return;
    }

    if (lineData?.id && Array.isArray(polylines)) {
      // Line view: derive available years from all polylines with matching coordinates
      const currentPolyline = polylines.find((p) => p.id === lineData.id);
      if (!currentPolyline) {
        setAvailableYears([]);
        setCurrentYear(null);
        return;
      }

      const currentCoords = JSON.stringify(currentPolyline.coordinates);
      const matchingYears = new Set();
      polylines.forEach((p) => {
        if (JSON.stringify(p.coordinates) === currentCoords) {
          matchingYears.add(Math.floor(Number(p.year) / 10) * 10);
        }
      });

      const sortedYears = Array.from(matchingYears).sort((a, b) => a - b);
      setAvailableYears(sortedYears);

      const decade = Math.floor(year / 10) * 10;
      if (sortedYears.includes(decade)) {
        setCurrentYear(decade);
      } else if (sortedYears.length > 0) {
        const closestYear = sortedYears.reduce((prev, curr) =>
          Math.abs(curr - decade) < Math.abs(prev - decade) ? curr : prev
        );
        setCurrentYear(closestYear);
      } else {
        setCurrentYear(null);
      }
      return;
    }

    // Marker view: derive available years from locationData
    if (!locationData || !markerIds || markerIds.length === 0) {
      setAvailableYears([]);
      setCurrentYear(null);
      return;
    }

    const flatMarkerIds = markerIds.flat();
    const yearsWithCurrentId = new Set();

    locationData.forEach((loc) => {
      if (loc.ids.some((id) => flatMarkerIds.includes(id))) {
        loc.years.forEach((y) =>
          yearsWithCurrentId.add(Math.floor(y / 10) * 10)
        );
      }
    });

    const sortedYears = Array.from(yearsWithCurrentId).sort((a, b) => a - b);
    setAvailableYears(sortedYears);

    const decade = Math.floor(year / 10) * 10;
    if (sortedYears.includes(decade)) {
      setCurrentYear(decade);
    } else if (sortedYears.length > 0) {
      const closestYear = sortedYears.reduce((prev, curr) =>
        Math.abs(curr - decade) < Math.abs(prev - decade) ? curr : prev
      );
      setCurrentYear(closestYear);
    } else {
      setCurrentYear(null);
    }
  }, [locationData, markerIds, year, polygonData, polygons, lineData, polylines]);

  const handlePrevDecade = () => {
    const currentDecade = Math.floor(year / 10) * 10;
    const earlierYears = availableYears.filter((y) => y < currentDecade);
    if (earlierYears.length > 0) {
      setYear(earlierYears[earlierYears.length - 1]);
    }
  };

  const handleNextDecade = () => {
    const currentDecade = Math.floor(year / 10) * 10;
    const laterYears = availableYears.filter((y) => y > currentDecade);
    if (laterYears.length > 0) {
      setYear(laterYears[0]);
    }
  };

  const handleAddStory = () => {
    const currentItem = selectedItems[currentPage];
    const isPolygon = currentItem?.shape_type === SHAPE_TYPE.POLYGON;
    const isLine = currentItem?.shape_type === SHAPE_TYPE.LINE;

    setAddStoryMode(true);
    setIsSidePanelFullscreen(false);

    if (isPolygon) {
      // Preserve polygon state before closing side panel
      const locId = currentItem?.loc_id;
      try {
        const coords = JSON.parse(locId);
        if (Array.isArray(coords) && coords.length >= 3) {
          setPolygonCoordinates(coords);
          setSelectionType(SHAPE_TYPE.POLYGON);

          const sumLat = coords.reduce((s, p) => s + (Array.isArray(p) ? p[0] : p.lat || 0), 0);
          const sumLng = coords.reduce((s, p) => s + (Array.isArray(p) ? p[1] : p.lng || 0), 0);
          const centroidLat = sumLat / coords.length;
          const centroidLng = sumLng / coords.length;
          if (!isNaN(centroidLat) && !isNaN(centroidLng)) {
            setSelectedAddressLatLng({ lat: centroidLat, lng: centroidLng });
          }
        }
      } catch {
        // fallback: keep selectionType as marker
      }
    } else if (isLine) {
      // Preserve line state before closing side panel
      const locId = currentItem?.loc_id;
      try {
        const coords = JSON.parse(locId);
        if (Array.isArray(coords) && coords.length >= 2) {
          if (setLineCoordinates) setLineCoordinates(coords);
          setSelectionType(SHAPE_TYPE.LINE);

          const sumLat = coords.reduce((s, p) => s + (Array.isArray(p) ? p[0] : p.lat || 0), 0);
          const sumLng = coords.reduce((s, p) => s + (Array.isArray(p) ? p[1] : p.lng || 0), 0);
          const centroidLat = sumLat / coords.length;
          const centroidLng = sumLng / coords.length;
          if (!isNaN(centroidLat) && !isNaN(centroidLng)) {
            setSelectedAddressLatLng({ lat: centroidLat, lng: centroidLng });
          }
        }
      } catch {
        // fallback: keep selectionType as marker
      }
    } else {
      setSelectionType(SHAPE_TYPE.MARKER);
      // Marker: resolve lat/lng from loc_id
      const locId = currentItem?.loc_id;
      if (locId) {
        const [lat, lng] = locId.split(",").map(Number);
        if (!isNaN(lat) && !isNaN(lng)) {
          setSelectedAddressLatLng({ lat, lng });
        }
      }
    }

    setShowContribution(true);
    setIsSidePanelOpen(false);

    // Extract visibility
    const visibility = currentItem?.visibility;
    if (visibility) {
      setStoryVisibility(visibility);
    }

    // Extract and set the address field
    const addressField = currentItem?.fields.find(
      (field) => field.field_type === "address"
    )?.field_value;
    if (addressField) {
      setSelectedAddress(addressField);
    }
  };

  const hasContent = currentItem.fields.some(
    (field) => field.field_type === "header"
  );

  const handleFullscreenToggle = () => {
    setIsSidePanelFullscreen(!isSidePanelFullscreen);
  };

  // Shareable URL copy — pairs with MapComponent's URL-sync effect that
  // keeps window.location in sync with the current selection + decade.
  // By the time the user can click this button, the URL already encodes
  // exactly what they see, so we just hand the address bar to clipboard.
  // The 2s "Copied!" pulse is intentional — toast would be louder than
  // this affordance deserves.
  const [linkCopied, setLinkCopied] = useState(false);
  const handleCopyShareLink = async () => {
    const href =
      typeof window !== "undefined" && window.location
        ? window.location.href
        : "";
    if (!href) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(href);
      } else {
        // Insecure context / older browser fallback. The textarea trick
        // is ancient but still the only thing that works without HTTPS
        // or modern Clipboard API.
        const el = document.createElement("textarea");
        el.value = href;
        el.style.position = "fixed";
        el.style.left = "-9999px";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (e) {
      console.error("Copy link failed:", e);
    }
  };

  const getFileSummary = (files) => {
    const fileCounts = {};
    files.forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      let type;
      if (ext === "pdf") type = "PDF";
      else if (
        ["doc", "docx", "txt", "rtf", "ppt", "pptx", "xls", "xlsx", "odt", "ods", "odp"].includes(ext)
      )
        type = "DOCUMENT";
      else type = ext.toUpperCase();
      fileCounts[type] = (fileCounts[type] || 0) + 1;
    });
    return Object.entries(fileCounts)
      .map(([type, count]) => `${count} ${type}`)
      .join(", ");
  };

  // Item 4 (revised per client 2026-06-22): CORE metadata — Location
  // (address) / Posted by (contributor) / Visibility (privacy). Stays
  // ALWAYS visible at the top of the panel (its original position), only
  // condensed via a slightly smaller font. The client asked that these
  // three never be concealed; only the thematic filter chips hide behind
  // the toggle (see renderTaxonomyChips below).
  const renderCoreMetadata = () => (
      <Box
        sx={{
          display: "inherit",
          flexDirection: isSidePanelFullscreen ? "row" : "inherit",
          gap: "inherit",
        }}
      >
        <Box sx={{ marginRight: isSidePanelFullscreen ? "auto" : "unset" }}>
          <Typography
            sx={{
              color: "var(--theme-color)",
              fontWeight: "700",
              textTransform: "capitalize",
              marginBottom: 1,
              fontSize: "0.75rem",
            }}
            variant="body2"
          >
            <strong>Location</strong>
          </Typography>
          <Typography
            sx={{
              color: "var(--theme-color)",
              fontSize: "0.75rem",
              fontStyle: isPolygonItem ? "italic" : "normal",
            }}
          >
            {locationDisplay}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "100px",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: "var(--theme-color)",
              textTransform: "capitalize",
              fontSize: "0.75rem",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
            variant="body2"
          >
            <strong>Posted by</strong>
            {Number.isFinite(Number(currentItem?.user?.user_id)) &&
            Number(currentItem.user.user_id) > 0 ? (
              <a
                href={`/u/${Number(currentItem.user.user_id)}`}
                style={{
                  color: "inherit",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                }}
              >
                {getUserName(currentItem)}
              </a>
            ) : (
              getUserName(currentItem)
            )}
          </Typography>

          {currentItem?.visibility && (
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                color: "var(--theme-color)",
                fontSize: "0.75rem",
                textTransform: "capitalize",
                textAlign: "center",
              }}
              variant="body2"
            >
              <strong>Visibility</strong>
              {currentItem.visibility === VISIBILITY.PRIVATE
                ? "Private"
                : currentItem.visibility === VISIBILITY.PROJECT
                  ? projectMapLabel
                  : "Public"}
              {currentItem.visibility === VISIBILITY.PRIVATE ? (
                <LockIcon />
              ) : currentItem.visibility === VISIBILITY.PROJECT ? (
                <MapIcon />
              ) : (
                <Public />
              )}
            </Typography>
          )}
        </Box>
      </Box>
  );

  // Item 4 (revised per client 2026-06-22): the taxonomy chips (Themes /
  // Property Types / Communities) are the ONLY metadata hidden behind the
  // "Show metadata" toggle, so the colored chips don't dominate by default
  // while address / contributor / privacy stay visible.
  // Returns a falsy value (renders nothing) when the story carries no
  // resolvable chips, so the caller can omit the toggle entirely.
  //
  // Each kind gets its own labeled row ("Themes" / "Property Types")
  // mirroring the core-metadata labels. Chips here are READ-ONLY descriptors
  // of the story — they render as filled badges in their taxonomy color and
  // do NOT toggle the global filter when clicked. We pass no onClick
  // (TaxonomyChip then drops its button role / tabIndex / hover affordance)
  // and pin isActive so every chip shows its full color.
  const renderTaxonomyChips = () =>
      Array.isArray(currentItem?.taxonomy_ids) &&
        currentItem.taxonomy_ids.length > 0 &&
        (() => {
          const visibleChips = currentItem.taxonomy_ids
            .map((id) => taxonomyIndex.get(Number(id)))
            .filter(Boolean);
          if (visibleChips.length === 0) return null;
          const themeChips = visibleChips.filter((t) => t.kind === "theme");
          const propertyChips = visibleChips.filter(
            (t) => t.kind === "property_type",
          );
          const communityChips = visibleChips.filter(
            (t) => t.kind === "community",
          );

          const renderRow = (label, rowChips) => {
            if (rowChips.length === 0) return null;
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  sx={{
                    color: "var(--theme-color)",
                    fontWeight: 700,
                    textTransform: "capitalize",
                    fontSize: "0.8rem",
                    flexShrink: 0,
                  }}
                  variant="body2"
                >
                  <strong>{label}</strong>
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                  }}
                >
                  {rowChips.map((t) => (
                    <TaxonomyChip
                      key={t.id}
                      name={t.name}
                      color={t.color}
                      isActive
                      isCatchAll={isCatchAllTaxonomy(t)}
                      size="sm"
                      ariaLabel={t.name}
                    />
                  ))}
                </Box>
              </Box>
            );
          };

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: "100%",
              }}
            >
              {renderRow("Themes", themeChips)}
              {renderRow("Property Types", propertyChips)}
              {renderRow("Communities", communityChips)}
            </Box>
          );
        })();

  if (!isSidePanelOpen) return null;

  return isEditing ? (
    <Grid
      item
      xs={12}
      sm={12}
      md={12}
      sx={{
        backgroundColor: "#f5f5f5",
        overflow: "hidden",
        position: "relative",
        height: "100%",
      }}
    >
      <Box sx={{ width: "100%", height: "100%", zIndex: 1000, position: "relative" }}>
        <ContributionComp
          editMode
          existingItem={currentItem}
          existingCarouselFiles={carouselFiles}
          existingNonImageFiles={nonImageFiles}
          onEditSave={(draft) => handleSaveChanges(draft)}
          onEditDelete={handleDelete}
          onEditCancel={() => setIsEditing(false)}
          onExistingFileDelete={handleExistingFileDelete}
          onExistingFileCaptionSave={handleFileCaptionSave}
          onFilesUploaded={fetchFiles}
          // Below props are required by ContributionComp's signature for the
          // create flow but unused in editMode. Pass safe no-ops so it renders
          // without errors.
          setShowContribution={() => {}}
          selectedAddress=""
          setSelectMode={() => {}}
          setSelectedAddress={() => {}}
          storyVisibility={currentItem?.visibility}
          clearSelectedAddress={() => {}}
          year={currentItem?.year || year}
          setYear={() => {}}
          setSelectedMarker={() => {}}
          selectedAddressLatLng={null}
          setSelectedAddressLatLng={() => {}}
          locations={[]}
          allowEmptyAddress
          refreshData={refreshData}
          addStoryMode={false}
          setAddStoryMode={() => {}}
          activatePolygonDrawing={() => {}}
          polygonCoordinates={[]}
          customSelectMode={false}
          setCustomSelectMode={() => {}}
          selectionType={currentItem?.shape_type || SHAPE_TYPE.MARKER}
        />
      </Box>
    </Grid>
  ) : (
    <Grid
      item
      xs={12}
      sm={12}
      md={12}
      className="side-panel-container"
      sx={{ background: "var(--theme-bg-header)" }}
    >
      <Box
        className="side-panel"
        sx={{
          width: "100%",
          maxWidth: isSidePanelFullscreen ? 1240 : undefined,
          height: isSidePanelFullscreen ? "100vh" : "calc(100vh - 152px)",
          margin: isSidePanelFullscreen ? "0 auto" : "",
          px: isSidePanelFullscreen ? 3 : 0,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: "15px",
        }}
      >
        {/* Header */}
        <Box className="side-panel-header" sx={{ flexShrink: "0" }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "5px",
              pt: 2,
              px: 2,
              pb: isSidePanelFullscreen ? 3 : 2,
              position: "relative",
            }}
          >
            {/* logo */}

            <Box
              sx={{
                height: "46px",
                width: "128px",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                left: "22pX",
                display: isSidePanelFullscreen ? "block" : "none",
                marginRight: isSidePanelFullscreen ? "auto" : "",
              }}
            >
              <img
                src="./MAP-250.svg"
                alt="Logo"
                style={{
                  height: "100px",
                  width: "100px",
                }}
              />

              <Typography
                sx={{
                  height: "17px",
                  width: "122px",
                  top: "55px",
                  left: "2px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  fontWeight: "bold",
                  fontSize: "13px",
                  position: "absolute",
                }}
              >
                {project?.project_name}
              </Typography>
            </Box>

            {/* logo End */}

            <IconButton
              aria-label="toggle-fullscreen"
              sx={{
                color: "var(--theme-color)",
                background: "#fff",
                borderRadius: "unset",
                p: 0.5,
              }}
              onClick={handleFullscreenToggle}
            >
              {isSidePanelFullscreen ? <CloseFullscreen /> : <Fullscreen />}
            </IconButton>

            {/* Right-rail action cluster.
             *
             * 2026-06-08 topbar coherence pass 3: the Share button used to
             * sit alone between the Fullscreen toggle and this cluster,
             * floating in the middle of the header row because the parent
             * uses `justify-content: space-between`. The client read it
             * as "an orphan that doesn't belong to either side". Moving
             * Share into this group puts it shoulder-to-shoulder with the
             * other secondary IconButtons (Edit) and the closer (Close),
             * so all panel-level actions read as a single cohesive
             * right-rail cluster. Visual hierarchy:
             *   Add A Story (primary CTA)
             *   Edit (conditional, owner-only)
             *   Share (secondary, universally available)
             *   Close (closer)
             */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                flexWrap: "wrap",
                gap: "6px",
                rowGap: "6px",
              }}
            >
              {hasContent && (
                <Button
                  className="side-panel-footer-button"
                  variant="contained"
                  onClick={handleAddStory}
                  startIcon={
                    <AddCircleIcon sx={{ fontSize: "1rem !important" }} />
                  }
                  sx={{
                    height: "36px",
                    minWidth: 0,
                    px: 1,
                    py: 0,
                    backgroundColor: "var(--theme-color) !important",
                    color: "var(--theme-btn-clr) !important",
                    fontWeight: "800 !important",
                    textTransform: "capitalize !important",
                    lineHeight: 1,
                    fontSize: "0.65rem",
                    flexShrink: 0,
                    "& .MuiButton-startIcon": { mr: "4px", ml: "-2px" },
                  }}
                >
                  Add A Story
                </Button>
              )}
              {canEditOrDelete() && currentItem.fields.length > 0 && (
                <IconButton
                  aria-label="edit"
                  onClick={handleEdit}
                  sx={{
                    color: "var(--theme-color)",
                    background: "#fff",
                    borderRadius: "unset",
                    p: 0.5,
                  }}
                >
                  <Edit />
                </IconButton>
              )}

              <Tooltip
                title={linkCopied ? "Copied!" : "Copy share link"}
                arrow
                placement="bottom"
              >
                <IconButton
                  aria-label={
                    linkCopied ? "share-link-copied" : "copy-share-link"
                  }
                  onClick={handleCopyShareLink}
                  sx={{
                    color: linkCopied ? "#2e7d32" : "var(--theme-color)",
                    background: "#fff",
                    borderRadius: "unset",
                    p: 0.5,
                  }}
                >
                  {linkCopied ? <Check /> : <Share />}
                </IconButton>
              </Tooltip>

              <IconButton
                aria-label="close"
                onClick={() => {
                  setIsSidePanelOpen(false);
                  setShowContribution(false);
                  setSelectedAddress(null);
                  setSelectedAddressLatLng(null);
                  setSelectedMarker(null);
                  setIsSidePanelFullscreen(false);
                }}
                sx={{
                  color: "var(--theme-color)",
                  background: "#fff",
                  borderRadius: "unset",
                  p: 0.5,
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>

          {/* header data section */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
              lineHeight: "1.4",
              mt: isSidePanelFullscreen ? 4 : 0,
              ...(hasContent && {
                borderBottom: "1px solid rgba(112, 112, 112, 0.15)",
                pb: 1.5,
              }),
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {(() => {
                const currentDecade = Math.floor(year / 10) * 10;
                const hasEarlierYears = availableYears.some(
                  (y) => y < currentDecade
                );
                return (
                  <IconButton
                    onClick={handlePrevDecade}
                    disabled={!hasEarlierYears}
                    sx={{ position: "relative", p: "0" }}
                  >
                    <NavigateBefore
                      sx={{
                        fontSize: 25,
                        fontWeight: hasEarlierYears ? 900 : 400,
                        opacity: !hasEarlierYears ? 0.3 : 1,
                        color: "var(--theme-color)",
                      }}
                    />
                  </IconButton>
                );
              })()}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "calc(100% - 65px)",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    fontSize: "20px",
                    fontWeight: "500",
                    color: "var(--theme-color)",
                    textAlign: "center",
                  }}
                >
                  {year === 2030 ? (
                    <>
                      Vision <br /> (Beyond 2020)
                    </>
                  ) : year === 1760 ? (
                    <>Pre-1770</>
                  ) : (
                    year + "s"
                  )}
                </Box>
              </Box>

              {(() => {
                const currentDecade = Math.floor(year / 10) * 10;
                const hasLaterYears = availableYears.some(
                  (y) => y > currentDecade
                );
                return (
                  <IconButton
                    onClick={handleNextDecade}
                    disabled={!hasLaterYears}
                    sx={{ position: "relative", p: "0" }}
                  >
                    <NavigateNext
                      sx={{
                        fontSize: 25,
                        fontWeight: hasLaterYears ? 900 : 400,
                        opacity: !hasLaterYears ? 0.3 : 1,
                        color: "var(--theme-color)",
                      }}
                    />
                  </IconButton>
                );
              })()}


            </Box>

            {/* header address section */}
            <Box
              sx={{
                fontSize: "22px",
                fontWeight: "700",
                color: "var(--theme-color)",
              }}
            >
              {hasContent ? (
                <>
                  <Typography
                    component="h2"
                    sx={{
                      width: "100%",
                      textAlign: "center",
                      font: "inherit",
                      color: "inherit",
                      fontWeight: "inherit",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {
                      currentItem.fields.find(
                        (field) => field.field_type === "header"
                      )?.field_value
                    }
                  </Typography>
                  {(() => {
                    const subtitleField = currentItem.fields.find(
                      (field) => {
                        const name = field.field_name?.toLowerCase() || "";
                        return name.includes("subtitle") || name.includes("caption");
                      }
                    );
                    const subtitleValue = subtitleField?.field_value?.trim();
                    return subtitleValue ? (
                      <Typography
                        component="p"
                        sx={{
                          width: "100%",
                          textAlign: "center",
                          fontSize: "0.95rem",
                          fontWeight: 500,
                          color: "var(--theme-color)",
                          opacity: 0.75,
                          marginTop: "4px",
                          fontStyle: "italic",
                        }}
                      >
                        {subtitleValue}
                      </Typography>
                    ) : null;
                  })()}
                </>
              ) : (
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{
                    width: "100%",
                    textAlign: "center",
                    font: "inherit",
                    color: "inherit",
                    fontWeight: "inherit",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {isPolygonItem ? locationDisplay : shortAddress || ""}
                </Typography>
              )}
            </Box>
            {/* header address section  End*/}
          </Box>
        </Box>

        {/* panel body */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            maxHeight: isSidePanelFullscreen
              ? "calc(100vh - 200px)"
              : "calc(100vh - 150px)",
            overflowY: "auto",
            scrollbarWidth: "thin",
            px: 4,
            pt: hasContent ? 1 : 0,
          }}
        >
          {currentItem?.approval_status &&
            currentItem.approval_status !== "approved" &&
            user &&
            currentItem.uid === user.uid && (
              <Box sx={{ mt: 0 }}>
                <Chip
                  label="Pending Approval"
                  size="small"
                  sx={{
                    backgroundColor: "#FFF3CD",
                    color: "#856404",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    border: "1px solid #FFEEBA",
                  }}
                />
              </Box>
            )}

          {/* Item 4 (revised per client 2026-06-22): core metadata —
              Location / Posted by / Visibility — is ALWAYS visible here at
              the top (its original position), condensed via a slightly
              smaller font. Only the taxonomy chips moved behind the
              "Show metadata" toggle near the comments section. */}
          {renderCoreMetadata()}

          {/* Images and recordings section */}
          {carouselFiles.length > 0 && (
            <Box
              className="carousel-container"
              sx={{
                // borderTop omitted: the always-on metadata divider
                // painted above the chip strip already provides the
                // upper edge of this section. Keeping a borderTop
                // here too would double-print the line.
                borderBottom: "2px solid #D9D6CF",
                py: 2,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    color: "var(--theme-color)",
                    fontWeight: "700",
                    marginRight: "auto",
                    marginBottom: 1,
                    fontSize: "0.8rem",
                  }}
                  variant="body2"
                >
                  <strong>Images and recordings</strong>
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {carouselFiles.length > 1 && (
                    <IconButton
                      onClick={handlePreviousCarousel}
                      disabled={currentCarouselIndex === 0}
                      sx={{ p: 0.5 }}
                    >
                      <NavigateBefore
                        sx={{
                          fontSize: 16,
                          fontWeight: currentCarouselIndex === 0 ? 400 : 900,
                          opacity: currentCarouselIndex === 0 ? 0.3 : 1,
                          color: "var(--theme-color)",
                        }}
                      />
                    </IconButton>
                  )}

                  <Typography
                    sx={{
                      color: "var(--theme-color)",
                      fontSize: "0.8rem",
                      textAlign: "center",
                    }}
                  >
                    {currentCarouselIndex + 1} of {carouselFiles.length}
                  </Typography>

                  {carouselFiles.length > 1 && (
                    <IconButton
                      onClick={handleNextCarousel}
                      disabled={
                        currentCarouselIndex === carouselFiles.length - 1
                      }
                      sx={{ p: 0.5 }}
                    >
                      <NavigateNext
                        sx={{
                          fontSize: 16,
                          fontWeight:
                            currentCarouselIndex === carouselFiles.length - 1
                              ? 400
                              : 900,
                          opacity:
                            currentCarouselIndex === carouselFiles.length - 1
                              ? 0.3
                              : 1,
                          color: "var(--theme-color)",
                        }}
                      />
                    </IconButton>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {carouselFiles[currentCarouselIndex]?.type === "image" && (
                  <Box
                    component="img"
                    src={carouselFiles[currentCarouselIndex]?.filepath}
                    alt={`Image ${currentCarouselIndex + 1}`}
                    onClick={() =>
                      handleImageClick(carouselFiles[currentCarouselIndex])
                    }
                    onContextMenu={(e) => e.preventDefault()}
                    draggable={false}
                    sx={{
                      maxWidth: "100%",
                      // Bigger photo in fullscreen — was a fixed 535px which
                      // wasted ~30% of vertical space on a 1080p monitor and
                      // ~50% on bigger displays. Cap to viewport height with
                      // a sensible absolute floor so the carousel arrows
                      // stay in view but photos breathe in fullscreen.
                      //
                      // Item 4c (client 2026-06-18): compact photo bumped
                      // 285px → min(60vh, 520px) so the whole image shows by
                      // default now that the photo leads the panel. object-
                      // fit:contain already prevents crop.
                      maxHeight: isSidePanelFullscreen
                        ? "min(78vh, 900px)"
                        : "min(60vh, 520px)",
                      objectFit: "contain",
                      cursor: "pointer",
                      userSelect: "none",
                      WebkitUserDrag: "none",
                    }}
                  />
                )}
                {carouselFiles[currentCarouselIndex]?.type === "video" && (
                  <Box key={carouselFiles[currentCarouselIndex]?.id} sx={{ width: "100%" }}>
                    <Plyr
                      source={{
                        type: "video",
                        sources: [{ src: carouselFiles[currentCarouselIndex]?.filepath }],
                      }}
                      options={{ controls: ["play-large", "play", "progress", "current-time", "mute", "volume", "fullscreen"] }}
                    />
                    <Typography
                      noWrap
                      sx={{
                        fontSize: "0.75rem",
                        color: "var(--theme-color)",
                        mt: 0.5,
                        width: "100%",
                      }}
                    >
                      {carouselFiles[currentCarouselIndex]?.name}
                    </Typography>
                  </Box>
                )}
                {carouselFiles[currentCarouselIndex]?.type === "audio" && (
                  <Box key={carouselFiles[currentCarouselIndex]?.id} sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        // Mirror the photo height bump so the audio
                        // placeholder card doesn't look squat next to images
                        // in the same carousel (item 4c: compact bumped to
                        // min(60vh, 520px) to match the photo).
                        height: isSidePanelFullscreen
                          ? "min(78vh, 900px)"
                          : "min(60vh, 520px)",
                        backgroundColor: "#fff",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src="./video.png"
                          alt="Audio file"
                          style={{ width: 80, height: 80, objectFit: "contain", opacity: 0.7 }}
                        />
                      </Box>
                      <Box sx={{ width: "100%" }}>
                        <Plyr
                          source={{
                            type: "audio",
                            sources: [{ src: carouselFiles[currentCarouselIndex]?.filepath }],
                          }}
                          options={{ controls: ["play", "progress", "current-time", "mute", "volume"] }}
                        />
                      </Box>
                    </Box>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: "0.75rem",
                        color: "var(--theme-color)",
                        mt: 0.5,
                        width: "100%",
                      }}
                    >
                      {carouselFiles[currentCarouselIndex]?.name}
                    </Typography>
                  </Box>
                )}

                {carouselFiles[currentCarouselIndex]?.caption && (
                  <Typography
                    sx={{
                      // Photo captions were small + italic + low-contrast,
                      // which made them feel like a footnote. Bump to a
                      // more legible weight, drop the italics, and use the
                      // primary theme color so they read as real content.
                      fontSize: isSidePanelFullscreen ? "1rem" : "0.9rem",
                      color: "var(--theme-color)",
                      fontStyle: "normal",
                      fontWeight: 500,
                      mt: 1,
                      width: "100%",
                      textAlign: "center",
                      lineHeight: 1.45,
                    }}
                  >
                    {carouselFiles[currentCarouselIndex].caption}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* loop data */}
          {hasContent &&
            currentItem.fields
              .filter(
                (field) => {
                  if (
                    field.field_type === "address" ||
                    field.field_type === "file" ||
                    field.field_type === "header"
                  ) return false;
                  // Subtitle/caption is rendered directly under the title;
                  // hide it from the generic field loop to avoid duplication.
                  const name = field.field_name?.toLowerCase() || "";
                  if (name.includes("subtitle") || name.includes("caption")) return false;
                  return true;
                }
              )
              .map((field, index) => (
                <Box key={index} className="side-panel-field">
                  <Typography
                    sx={{
                      color: "var(--theme-color)",
                      fontWeight: "700",
                      textTransform: "capitalize",
                      marginBottom: 1,
                      fontSize: "0.8rem",
                    }}
                    variant="body2"
                  >
                    <strong>
                      {field.field_name.charAt(0).toUpperCase() +
                        field.field_name.slice(1)}
                    </strong>
                  </Typography>

                  <Typography
                    sx={{
                      color: "var(--theme-color)",
                      // Magazine-style body in fullscreen — larger size +
                      // looser leading + serif-ish line measure makes
                      // long stories readable without forcing a copyedit.
                      // Compact mode stays 1rem to preserve current sidebar.
                      fontSize: isSidePanelFullscreen ? "1.15rem" : "1rem",
                      lineHeight: isSidePanelFullscreen ? 1.65 : 1.5,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {renderTextWithLinks(field.field_value)}
                  </Typography>
                </Box>
              ))}
          {nonImageFiles.length > 0 &&
            (() => {
              const summary = getFileSummary(nonImageFiles);
              return (
                <Box
                  sx={{
                    borderTop: "2px solid #D9D6CF",
                    borderBottom: "2px solid #D9D6CF",
                    py: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "var(--theme-color)",
                        fontWeight: "700",
                        textTransform: "capitalize",
                        marginBottom: 1,
                        fontSize: "0.8rem",
                      }}
                      variant="body2"
                    >
                      <strong>Files</strong>
                    </Typography>
                    <Typography
                      sx={{
                        color: "var(--theme-color)",
                        marginBottom: 1,
                        fontSize: "0.7rem",
                        lineHeight: "100%",
                      }}
                      variant="body2"
                    >
                      {summary}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      gap: "20px",
                      overflowX: "auto",
                      flexWrap: "nowrap",
                      paddingLeft: "5px",
                    }}
                  >
                    {nonImageFiles.map((file, idx) => (
                      <Box
                        key={idx}
                        component="a"
                        href={file.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          textDecoration: "none",
                          color: "inherit",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "85px",
                          cursor: "pointer",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 60,
                            width: 60,
                          }}
                        >
                          <img
                            src="./file.png"
                            alt={file.name}
                            style={{
                              height: "100%",
                              width: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </Box>
                        <Typography
                          noWrap
                          sx={{
                            maxWidth: "100%",
                            fontSize: "0.7rem",
                            color: "var(--theme-color)",
                            textAlign: "center",
                            mt: 1,
                          }}
                        >
                          {file.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })()}
          {/* Item 4 (revised per client 2026-06-22): the "Show metadata"
              toggle controls ONLY the taxonomy chips — address / contributor
              / privacy render always-on at the top. The toggle (and its
              divider) is omitted entirely when the story has no chips to
              reveal. Defaults HIDDEN on every mount (not persisted) so the
              colored chips never dominate by default. */}
          {(() => {
            const chips = renderTaxonomyChips();
            if (!chips) return null;
            return (
              <>
                <Box
                  aria-hidden="true"
                  sx={{
                    borderBottom: "2px solid #D9D6CF",
                    width: "100%",
                  }}
                />
                <Box>
                  <Button
                    onClick={toggleShowMetadata}
                    aria-expanded={showMetadata}
                    disableRipple
                    sx={{
                      color: "var(--theme-color)",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      textTransform: "none",
                      p: 0,
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    {showMetadata ? "Hide metadata" : "Show metadata"}
                    <span
                      aria-hidden="true"
                      style={{
                        display: "inline-block",
                        transform: showMetadata ? "rotate(180deg)" : "none",
                        transition: "transform 120ms ease-out",
                        fontSize: "0.7rem",
                      }}
                    >
                      {"\u25BE"}
                    </span>
                  </Button>
                  {showMetadata && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        mt: 1.5,
                      }}
                    >
                      {chips}
                    </Box>
                  )}
                </Box>
              </>
            );
          })()}

          <Box>
            {currentItem.id && <Comments submission_id={currentItem.id} />}
          </Box>
        </Box>
        {/* panel body ENd*/}

        {/* Footer */}
        <Box
          className="side-panel-footer"
          sx={{
            p: "5px",
            backgroundColor: "var(--theme-bg-header)",
            borderTop: "1px solid rgba(112, 112, 112, 0.3)",
            display: "flex",
            flexDirection: "column",
            gap: "5px",
            marginTop: "auto",
          }}
        >
          <Box className="navigation-controls">
            <Button
              onClick={handlePrevious}
              disabled={currentPage === 0}
              sx={{
                color: "var(--theme-color)",
                fontSize: "0.75rem",
                textTransform: "capitalize",
                textDecoration: "underline",
                p: 0,
              }}
            >
              Previous
            </Button>
            <Typography sx={{ fontSize: "0.75rem" }}>{`${currentPage + 1} of ${selectedItems.length
              }`}</Typography>
            <Button
              onClick={handleNext}
              disabled={currentPage >= selectedItems.length - 1}
              sx={{
                color: "var(--theme-color)",
                fontSize: "0.75rem",
                textTransform: "capitalize",
                textDecoration: "underline",
                p: 0,
              }}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={imageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Image Viewer</DialogTitle>
        <DialogContent>
          <img
            src={selectedImage?.filepath}
            alt="Enlarged View"
            className="dialog-image"
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "80vh",
              objectFit: "contain",
              userSelect: "none",
              WebkitUserDrag: "none",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Grid>
  );
};
export default SidePanelComponent;
